import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  QUESTION_MATRIX_OPEN_ENDED_SIGN_PLACEMENTS,
  QUESTION_MATRIX_OPEN_ENDED_NUMERIC_EXCLUDE_SYMBOLS,
} from '../constants';
import { roundNumberToStringWithDecimals } from '../../../../../utils';
import { onScrollDownMessage } from '../utils';

const integerPattern = '[0-9]*';
const decimalPattern = '[0-9]+([.,][0-9]+)?';
export default function OpenEndedNumericInput({
  min,
  max,
  decimals = 0,
  rowId,
  rowRefId,
  rowOrder,
  columnId,
  columnRefId,
  columnOrder,
  openEndedSignPlacement,
  openEndedSign,
  required,
  inputStyle,
  onChange,
}) {
  const [value, setValue] = useState();
  const signRef = useRef(null);
  const hasDecimals = decimals > 0;

  // In <input type="number"> onChange event, the event.target.value
  // does not contains the below excluded characters so we cannot
  // put all the functionality inside onChange
  const handleKeyDown = useCallback((event) => {
    const isExcluded = QUESTION_MATRIX_OPEN_ENDED_NUMERIC_EXCLUDE_SYMBOLS.includes(
      event.key,
    );
    if (isExcluded) {
      event.preventDefault();
    }
  }, []);

  const handleWheel = useCallback(() => {
    if (signRef.current) {
      signRef.current.blur();
    }
  }, []);

  const handleBlurClick = useCallback(
    (e) => {
      let updatedValue = roundNumberToStringWithDecimals(
        e.target.value,
        decimals,
      );
      if (updatedValue === 'NaN') {
        updatedValue = '';
      }
      if (updatedValue !== '') {
        // We need to remove the red border of the input when it has a value
        // NOTE: This is a temporary solution and should be changed in the future,
        // in react way we should save the value and the error flag
        // into the parent state and then pass it to the child component
        e.target.parentElement.classList.remove(
          'answer--matrix-open-ended-answer-input-container-required',
        );
      }
      onScrollDownMessage();
      setValue(updatedValue);
      if (onChange) {
        onChange(columnOrder, updatedValue);
      }
    },
    [columnOrder, decimals, onChange],
  );

  const handleSignClick = useCallback(() => {
    if (signRef.current) {
      signRef.current.focus();
    }
  }, []);

  let inputElement = (
    <input
      ref={signRef}
      data-row-id={rowId}
      data-row-ref-id={rowRefId}
      data-row-order={rowOrder}
      data-column-id={columnId}
      data-column-ref-id={columnRefId}
      data-column-order={columnOrder}
      data-input-required={required}
      className={`answer--matrix-open-ended-answer-input ${inputStyle}`}
      type="number"
      min={min}
      max={max}
      inputMode="numeric"
      pattern={hasDecimals ? decimalPattern : integerPattern}
      step={hasDecimals ? '0.01' : '1'}
      onKeyDown={handleKeyDown}
      onBlur={handleBlurClick}
      onWheel={handleWheel}
      onChange={(e) => setValue(e.target.value)}
      value={value}
    />
  );

  if (Pollfish.helper.json.sdk_version < 17 && Pollfish.helper.isAndroid()) {
    inputElement = (
      <textarea
        ref={signRef}
        rows="1"
        data-row-id={rowId}
        data-row-ref-id={rowRefId}
        data-row-order={rowOrder}
        data-column-id={columnId}
        data-column-ref-id={columnRefId}
        data-column-order={columnOrder}
        data-input-required={required}
        className={`answer--matrix-open-ended-answer-input ${inputStyle}`}
        onKeyDown={handleKeyDown}
        onBlur={handleBlurClick}
        onWheel={handleWheel}
        onChange={(e) => setValue(e.target.value)}
        value={value}
      ></textarea>
    );
  }

  return (
    <div className="answer--matrix-open-ended-answer-input-container form-container">
      {openEndedSignPlacement ===
        QUESTION_MATRIX_OPEN_ENDED_SIGN_PLACEMENTS.LEFT && (
        <button
          type="button"
          tabIndex={-1}
          onClick={handleSignClick}
          className="answer--matrix-open-ended-answer-sign answer--matrix-open-ended-answer-sign-left formatted-answers-inline"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: openEndedSign || '&nbsp;' }}
        />
      )}
      {inputElement}
      {openEndedSignPlacement ===
        QUESTION_MATRIX_OPEN_ENDED_SIGN_PLACEMENTS.RIGHT && (
        <button
          type="button"
          tabIndex={-1}
          onClick={handleSignClick}
          className="answer--matrix-open-ended-answer-sign answer--matrix-open-ended-answer-sign-right formatted-answers-inline"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: openEndedSign || '&nbsp;' }}
        />
      )}
    </div>
  );
}

OpenEndedNumericInput.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  decimals: PropTypes.number,
  rowId: PropTypes.number,
  rowRefId: PropTypes.string,
  rowOrder: PropTypes.number,
  columnId: PropTypes.number,
  columnRefId: PropTypes.string,
  columnOrder: PropTypes.number,
  openEndedSignPlacement: PropTypes.string,
  openEndedSign: PropTypes.string,
  required: PropTypes.bool,
  inputStyle: PropTypes.string,
  onChange: PropTypes.func,
};
